import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { useCurrencyStore } from "../stores/currencyStore"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Date format preferences
 */
export type DateFormatPreference = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
export type TimeFormatPreference = '12h' | '24h';

/**
 * Get user preferences from localStorage
 */
function getUserPreferences() {
  try {
    const authUser = localStorage.getItem('auth_user');
    if (authUser) {
      const user = JSON.parse(authUser);
      return {
        dateFormat: (user.date_format || 'MM/DD/YYYY') as DateFormatPreference,
        timeFormat: (user.time_format || '12h') as TimeFormatPreference,
        timezone: user.timezone || 'America/New_York'
      };
    }
  } catch (e) {
    console.error('Error getting user preferences:', e);
  }
  return {
    dateFormat: 'MM/DD/YYYY' as DateFormatPreference,
    timeFormat: '12h' as TimeFormatPreference,
    timezone: 'America/New_York'
  };
}

/**
 * Convert date format preference to Intl.DateTimeFormat options
 */
function getDateFormatOptions(format: DateFormatPreference): Intl.DateTimeFormatOptions {
  switch (format) {
    case 'MM/DD/YYYY':
      return { month: '2-digit', day: '2-digit', year: 'numeric' };
    case 'DD/MM/YYYY':
      return { day: '2-digit', month: '2-digit', year: 'numeric' };
    case 'YYYY-MM-DD':
      return { year: 'numeric', month: '2-digit', day: '2-digit' };
    default:
      return { month: '2-digit', day: '2-digit', year: 'numeric' };
  }
}

/**
 * Format date according to user preferences
 */
export function formatDate(date: Date | string, options?: { includeTime?: boolean; shortFormat?: boolean }): string {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Invalid Date';
  
  const prefs = getUserPreferences();
  
  if (options?.shortFormat) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(d);
  }
  
  const formatOptions = getDateFormatOptions(prefs.dateFormat);
  
  if (options?.includeTime) {
    const timeOptions = prefs.timeFormat === '12h' 
      ? { hour: 'numeric', minute: '2-digit', hour12: true } as const
      : { hour: '2-digit', minute: '2-digit', hour12: false } as const;
    
    return new Intl.DateTimeFormat('en-US', {
      ...formatOptions,
      ...timeOptions
    }).format(d);
  }
  
  return new Intl.DateTimeFormat('en-US', formatOptions).format(d);
}

/**
 * Format time according to user preferences
 */
export function formatTime(date: Date | string): string {
  if (!date) return '--:--';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Invalid Time';
  
  const prefs = getUserPreferences();
  
  const options = prefs.timeFormat === '12h'
    ? { hour: 'numeric', minute: '2-digit', hour12: true } as const
    : { hour: '2-digit', minute: '2-digit', hour12: false } as const;
  
  return new Intl.DateTimeFormat('en-US', options).format(d);
}

/**
 * Format date for display (short version with month name)
 */
export function formatDateShort(date: Date | string): string {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Invalid Date';
  
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
}

/**
 * Format datetime according to user preferences
 */
export function formatDateTime(date: Date | string): string {
  return formatDate(date, { includeTime: true });
}

/**
 * Format date for API (YYYY-MM-DD)
 */
export function formatDateForAPI(date: Date | string): string {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

export function formatCurrency(amount: number): string {
  const currency = useCurrencyStore.getState().currencyCode;

  const LOCALE_MAP: Record<string, string> = {
    USD: 'en-US', EUR: 'de-DE', GBP: 'en-GB', JPY: 'ja-JP', CAD: 'en-CA',
    AUD: 'en-AU', CHF: 'de-CH', CNY: 'zh-CN', INR: 'en-IN', LKR: 'si-LK',
    SGD: 'en-SG', AED: 'ar-AE', MYR: 'ms-MY', BRL: 'pt-BR', ZAR: 'en-ZA',
  };
  const locale = LOCALE_MAP[currency] || 'en-US';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount);
}

export function getInitials(firstName: string, lastName?: string): string {
  if (!lastName) {
    return firstName.charAt(0).toUpperCase();
  }
  return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
}

export function getFullName(firstName: string, lastName?: string): string {
  return lastName ? `${firstName} ${lastName}` : firstName;
}